// ============================================================
// Parking the stage - the live table becomes a scene in the wings.
// ============================================================
import type { Combatant } from '../roster/types';
import type { Terrain } from '../battlefield/presets';
import { preserveMarker, type TeamArrivals } from '../battlefield/deployment';
import { emptyTurnTracker } from '../factories';
import { removeParticipant } from '../turnTracker';
import type { HiddenMark, ParkedScene, SceneRecord, StageState } from './types';

/** A participant's id in the tracker, from the combatant's - the tracker's own convention. */
export const participantIdOf = (combatantId: string): string => `tab-${combatantId}`;

/**
 * A scene nobody has stood on yet.
 *
 * The marker is the map's suggestion when it carries one - the same rule
 * `loadMap` applies to a session with no marker - since a new stage has
 * none of its own to preserve.
 */
export function emptyScene(
  id: string,
  name: string,
  terrain: Terrain,
  mapName: string | null,
  order: number,
  now: number,
): SceneRecord {
  return {
    id,
    name,
    order,
    notes: '',
    narration: '',
    mapName,
    marker: preserveMarker(null, terrain),
    tracker: emptyTurnTracker(),
    marks: {},
    castIds: [],
    lastOnStageAt: null,
    createdAt: now,
    phase: null,
    hidden: {},
  };
}

/**
 * Freeze the stage into `scene`.
 *
 * Everyone on the roster becomes the cast, and everyone's token becomes a
 * mark - INCLUDING the travellers, who are about to leave for another
 * stage: their mark is what stands them back here when they return.
 * Travellers themselves are not parked (a person is live on exactly one
 * stage) and their chips leave the parked order, as a recall's do.
 *
 * Mutates nothing. The record keeps its own name, notes, order and
 * creation time; only what the stage knows is overwritten.
 */
export function parkStage(
  stage: StageState,
  scene: SceneRecord,
  now: number,
  travellers: ReadonlySet<string> = new Set(),
): ParkedScene {
  const staying = stage.roster.filter((c) => !travellers.has(c.id));
  let tracker = stage.tracker;
  for (const c of stage.roster) {
    if (travellers.has(c.id)) tracker = removeParticipant(tracker, participantIdOf(c.id));
  }
  // The table's team points ARE the scene's now: none on the table means none kept from the last park.
  const { arrivals: _lastPark, ...rest } = scene;
  return {
    record: {
      ...rest,
      marks: marksOf(stage, scene.marks),
      castIds: staying.map((c: Combatant) => c.id),
      hidden: onlyThese(stage.hidden, staying),
      tracker,
      marker: stage.marker,
      ...withTeamPoints(stage.arrivals),
      lastOnStageAt: now,
    },
    cast: staying,
    terrain: stage.terrain,
  };
}

/** Where everyone on the roster stands now, over where the scene last saw anyone (a traveller's mark is kept). */
function marksOf(stage: StageState, earlier: SceneRecord['marks']): SceneRecord['marks'] {
  const marks = { ...earlier };
  for (const c of stage.roster) {
    const token = stage.tokens[c.id];
    if (token) marks[c.id] = token;
  }
  return marks;
}

/** The hidden marks of these people only (a hidden traveller is not parked here). */
export function onlyThese(hidden: StageState['hidden'], people: readonly Combatant[]): Record<string, HiddenMark> {
  if (!hidden) return {};
  return Object.fromEntries(people.filter((c) => hidden[c.id]).map((c) => [c.id, hidden[c.id]]));
}

/**
 * Nothing on the table worth parking: nobody, no token, no cube, no
 * solid, no painted ground. A session that has only just opened, or a
 * stage NEW SCENE just raised and nobody built on.
 */
export function stageIsEmpty(stage: StageState): boolean {
  const t = stage.terrain;
  const grid = t.voxels;
  if (stage.roster.length !== 0 || Object.keys(stage.tokens).length !== 0) return false;
  const built = [t.solids?.length ?? 0, t.zones?.length ?? 0, Object.keys(grid?.cells ?? {}).length];
  return built.every((n) => n === 0) && Object.keys(grid?.dug ?? {}).length === 0;
}

/** Team arrival points as a record carries them: spread, so a scene with none keeps exactly its keys (B). */
export function withTeamPoints(arrivals: TeamArrivals | null | undefined): { arrivals?: TeamArrivals } {
  return arrivals && Object.keys(arrivals).length ? { arrivals } : {};
}
